import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TimeSheetStatusEnum } from '../../time-sheets/time-sheet-status.enum';
import { TimeSheetStatusTextEnum } from '../../time-sheets/time-sheet-status-text.enum';
import { MatSort } from '@angular/material/sort';
import * as XLSX from 'xlsx';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { IPilot } from '../../pay-scales/pay-scales.types';
import { MatTableDataSource } from '@angular/material/table';
import * as moment from 'moment';
import { EmploymentStatusEnum } from '../../override-employees/EmploymentStatusEnum';

@Component({
	selector: 'app-pilots-table',
	templateUrl: './pilots-table.component.html',
	styleUrls: ['./pilots-table.component.scss']
})
export class PilotsTableComponent implements OnChanges {
	constructor() {}

	get timeSheetStatusEnum(): any {
		return TimeSheetStatusEnum;
	}

	get timeSheetStatusTextEnum(): any {
		return TimeSheetStatusTextEnum;
	}

	get employmentStatuses(): any {
		return EmploymentStatusEnum;
	}

	@Output() onExportPayroll: EventEmitter<number[]> = new EventEmitter();
	@Input() pilots: MatTableDataSource<IPilot>;
	@Input() selectedDate: moment.Moment;
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild('pilotsTable', { static: true }) table: ElementRef;

	public isExportToPayroll: boolean;
	public displayedPilotsColumns: string[] = [
		'checked',
		'lastName',
		'timeSheetStatus',
		'perDiemHours',
		'guaranteedHours',
		'totalPay'
	];
	public previousPayPeriodMonth: any = moment()
		.subtract(1, 'month')
		.format('M');

	public selectAllPilots: boolean = false;
	public filterText: string = '';
	public serializedDate: string;
	public filterStatus: string = 'All';

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.pilots && changes.pilots.currentValue) {
			const pilots: IPilot[] = changes.pilots.currentValue.filter(p => p.showInLists);
			this.pilots = new MatTableDataSource<IPilot>(pilots);
			this.setFilterAndSort();
		}

		if (changes.selectedDate && changes.selectedDate.currentValue) {
			this.selectedDate = changes.selectedDate.currentValue;
			this.serializedDate = this.selectedDate.utc(true).format('YYYY-MM-DDTHH:mm:ss');
			this.selectAllPilots = false;
		}
	}

	public checkExportToPayRoll(): void {
		this.isExportToPayroll = false;
		if (this.selectedDate.format('M') === this.previousPayPeriodMonth) {
			this.isExportToPayroll = true;
			if (!this.displayedPilotsColumns.includes('checked')) {
				this.displayedPilotsColumns.splice(0, 0, 'checked');
			}
			const mappedPilots: IPilot[] = this.pilots.data.map(pilot => ({
				...pilot,
				checked: false
			}));
			this.pilots = new MatTableDataSource<IPilot>(mappedPilots);
			this.displayedPilotsColumns.includes('unchecked');
		}
	}

	public filterPilots(): void {
		this.pilots.filter = this.filterText ? this.filterText.trim().toLocaleLowerCase() : this.filterStatus;
	}

	public getTextFilterPredicate(data: any): any {
		let textPredicate: any = '';

		if (this.filterText) {
			textPredicate =
				data.firstName.toLowerCase().indexOf(this.filterText.trim().toLocaleLowerCase()) !== -1 ||
				data.lastName.toLowerCase().indexOf(this.filterText.trim().toLocaleLowerCase()) !== -1 ||
				data.email.indexOf(this.filterText.trim().toLocaleLowerCase()) !== -1 ||
				data.payrollNumber.indexOf(this.filterText.trim().toLocaleLowerCase()) !== -1 ||
				(this.displayedPilotsColumns.includes('jobTitleName') &&
					data.jobTitleName.toLowerCase().indexOf(this.filterText.trim().toLocaleLowerCase()) !== -1) ||
				(this.displayedPilotsColumns.includes('aircraftModelName') &&
					data.aircraftModelName.toLowerCase().indexOf(this.filterText.trim().toLocaleLowerCase()) !== -1);
			if (textPredicate) {
				switch (this.filterStatus) {
					case 'Not Started':
						return (
							(data.timeSheetStatus === this.timeSheetStatusEnum.NotStarted ||
								data.timeSheetStatus === null) &&
							textPredicate
						);
					case 'Paid':
						return data.timeSheetStatus === this.timeSheetStatusEnum.Paid && textPredicate;
					case 'Not Paid':
						return data.timeSheetStatus !== this.timeSheetStatusEnum.Paid && textPredicate;
					case 'Approved':
						return data.timeSheetStatus === this.timeSheetStatusEnum.ApprovedByAdmin && textPredicate;
					default:
						return data;
				}
			}
		} else if (this.filterStatus) {
			switch (this.filterStatus) {
				case 'Not Started':
					return (
						data.timeSheetStatus === this.timeSheetStatusEnum.NotStarted || data.timeSheetStatus === null
					);
				case 'Paid':
					return data.timeSheetStatus === this.timeSheetStatusEnum.Paid;
				case 'Not Paid':
					return data.timeSheetStatus !== this.timeSheetStatusEnum.Paid;
				case 'Approved':
					return data.timeSheetStatus === this.timeSheetStatusEnum.ApprovedByAdmin;
				default:
					return data;
			}
		}
	}

	public copyEmails(): void {
		const checkbx: any = [];
		this.pilots.filteredData.forEach(pilot => {
			checkbx.push(pilot.email);
		});
		const dummy: any = document.createElement('input');
		document.body.appendChild(dummy);
		dummy.setAttribute('id', 'dummy_id');
		(<HTMLInputElement>document.getElementById('dummy_id')).value = checkbx.join(';');
		dummy.select();
		document.execCommand('copy');
		document.body.removeChild(dummy);
	}

	public exportToExcel(): void {
		const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(this.table.nativeElement);
		const wb: XLSX.WorkBook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

		/* save to file */
		XLSX.writeFile(wb, 'pilots.xlsx');
	}

	public statusFilter(filteredStatus: string): void {
		this.pilots.filter = this.filterStatus ? filteredStatus : this.filterText.toLowerCase();
	}

	public toggleColumn($event: MatCheckboxChange, name: string): void {
		this.addOrDeleteColumn(name, !$event.checked);
	}

	public exportToPayroll(): void {
		const selectedPilotsId: number[] = this.pilots.filteredData
			.filter(pilot => pilot.checked)
			.map(p => p.employeeId);
		this.onExportPayroll.emit(selectedPilotsId);
	}

	public selectAll(event: MatCheckboxChange): void {
		this.pilots.data = this.pilots.data.map(pilot => ({
			...pilot,
			checked: event.checked
		}));
	}

	public onSelectPilotChange(): void {
		this.selectAllPilots = !this.pilots.data.find(pilot => !pilot.checked);
	}

	private addOrDeleteColumn(columnName: string, deleteColumn: boolean): void {
		if (!deleteColumn) {
			this.displayedPilotsColumns.splice(2, 0, columnName);
		} else {
			const index: number = this.displayedPilotsColumns.findIndex(item => item === columnName);
			this.displayedPilotsColumns.splice(index, 1);
		}
	}

	private setFilterAndSort(): void {
		this.checkExportToPayRoll();
		this.pilots.sort = this.sort;

		// Sets a custom filter predicate
		this.pilots.filterPredicate = (data: any) => {
			return this.getTextFilterPredicate(data);
		};
		this.filterPilots();
		this.statusFilter('All');
	}
}
